import { Building2, Check, RefreshCw, UserPlus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { WalkInApprovalStatus } from '@/components/guard/WalkInApprovalStatus';
import { useGuardContext } from '@/hooks/useGuardContext';
import { useWalkInStatus } from '@/hooks/useWalkInStatus';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { normalizeKePhoneToE164 } from '@/lib/phone';
import { isSingleTenant } from '@/lib/property-type';
import {
  createWalkIn,
  getGuardUnits,
  getPropertyConfig,
  getGuardUnitEmployees,
} from '@/services/api.client';
import type { CreateWalkInBody, WalkInRequest } from '@/types';

// ─── Phone validation ────────────────────────────────────────────────────────
// Accepts any common Kenyan shape (07…, 01…, 254…, +254…) via the shared
// utility; the guard should never have a valid local number rejected at the gate.

function validatePhone(phone: string): boolean {
  return normalizeKePhoneToE164(phone) !== null;
}

// ─── ID types ────────────────────────────────────────────────────────────────

const ID_TYPES = [
  { value: 'national_id', labelKey: 'guard.walkIn.idNational' },
  { value: 'passport', labelKey: 'guard.walkIn.idPassport' },
  { value: 'military_id', labelKey: 'guard.walkIn.idMilitary' },
] as const;

// ─── Walk-in state persistence via sessionStorage ───────────────────────────
// Survives page remounts (e.g. guard navigates to queue and back during approval).

const WALK_IN_STORAGE_KEY = 'securegate-guard-pending-walkin';

function readPendingWalkIn(): WalkInRequest | null {
  try {
    const raw = sessionStorage.getItem(WALK_IN_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as WalkInRequest) : null;
  } catch {
    return null;
  }
}

function writePendingWalkIn(walkIn: WalkInRequest | null): void {
  try {
    if (walkIn) {
      sessionStorage.setItem(WALK_IN_STORAGE_KEY, JSON.stringify(walkIn));
    } else {
      sessionStorage.removeItem(WALK_IN_STORAGE_KEY);
    }
  } catch {
    // sessionStorage unavailable — silent
  }
}

// ─── Form state ──────────────────────────────────────────────────────────────

interface FormState {
  visitorName: string;
  visitorPhone: string;
  idType: string;
  idNumber: string;
  unitId: string;
  /** Only set when the selected unit is enterprise-typed and guard picks a host employee */
  hostEmployeeUserId: string;
  vehiclePlate: string;
}

interface FormErrors {
  visitorName?: string;
  visitorPhone?: string;
  unitId?: string;
}

const INITIAL_FORM: FormState = {
  visitorName: '',
  visitorPhone: '',
  idType: '',
  idNumber: '',
  unitId: '',
  hostEmployeeUserId: '',
  vehiclePlate: '',
};

/**
 * Walk-In Registration — Guard walk-in visitor form with approval flow.
 * Requirements: 4.1–4.8, 11.2
 */
export function WalkInPage() {
  const { t } = useTranslation();
  const { propertyId, isOnline } = useGuardContext();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  // pendingWalkIn is initialised from sessionStorage so it survives remounts
  const [pendingWalkIn, setPendingWalkIn] = useState<WalkInRequest | null>(readPendingWalkIn);

  // Unit combobox state
  const [unitSearch, setUnitSearch] = useState('');
  const [unitOpen, setUnitOpen] = useState(false);
  const unitRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (unitRef.current && !unitRef.current.contains(e.target as Node)) {
        setUnitOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch units matching the guard's current search text. Guards hit
  // /guard/units/search (NOT /properties/:id/units which is PM-only). The
  // server already handles an empty `q` by returning an initial page, so we
  // always fire the request and let the dropdown filter server-side. Keying
  // on `unitSearch` makes React Query refetch as the guard types; a short
  // staleTime stops the dropdown from flashing when a stale hit races in.
  const { data: units = [] } = useQuery({
    queryKey: ['guard', 'units', propertyId, unitSearch],
    queryFn: () => getGuardUnits(unitSearch),
    enabled: !!propertyId,
    staleTime: 10_000,
  });

  // Property config — single-tenant enterprise buildings short-circuit the
  // "which unit?" prompt because there is only one (the company that occupies
  // the building). ADR 009 slice 2.
  const { data: propertyConfig } = useQuery({
    queryKey: ['property-config', propertyId],
    queryFn: () => getPropertyConfig(propertyId),
    enabled: !!propertyId,
  });
  const isSingleTenantBuilding = isSingleTenant(propertyConfig?.propertyType) && units.length === 1;
  const soleUnitId = isSingleTenantBuilding ? (units[0]?.id ?? undefined) : undefined;
  const buildingCompanyName = propertyConfig?.name;

  // Auto-fill unitId on single-tenant buildings — the sole company unit was
  // created at property setup, so the guard should never need to pick.
  useEffect(() => {
    if (soleUnitId && form.unitId !== soleUnitId) {
      setForm((prev) => ({ ...prev, unitId: soleUnitId }));
    }
  }, [soleUnitId, form.unitId]);

  // Units filtered by current search term
  const filteredUnits = units.filter(
    (u) =>
      u.unitReference.toLowerCase().includes(unitSearch.toLowerCase()) ||
      (u.zoneName ?? '').toLowerCase().includes(unitSearch.toLowerCase()),
  );

  function handleUnitSelect(unitId: string, label: string) {
    setForm((prev) => ({ ...prev, unitId }));
    setUnitSearch(label);
    setUnitOpen(false);
    if (errors.unitId) setErrors(({ unitId: _omit, ...rest }) => rest);
  }

  function handleUnitSearchChange(value: string) {
    setUnitSearch(value);
    setUnitOpen(true);
    // Clear selection if user edits away from the chosen unit label
    if (!value) {
      setForm((prev) => ({ ...prev, unitId: '' }));
    } else {
      const selected = units.find((u) => u.id === form.unitId);
      if (selected && !selected.unitReference.toLowerCase().includes(value.toLowerCase())) {
        setForm((prev) => ({ ...prev, unitId: '' }));
      }
    }
    if (errors.unitId) setErrors(({ unitId: _omit, ...rest }) => rest);
  }

  // ── Enterprise employee lookup ──
  // When the selected unit is enterprise-typed, fetch active employees so the
  // guard can optionally route the walk-in to a specific person.
  const { data: enterpriseEmployees = [] } = useQuery({
    queryKey: ['guard', 'unit-employees', form.unitId],
    queryFn: () => getGuardUnitEmployees(form.unitId),
    enabled: !!form.unitId && !isSingleTenantBuilding,
    // Silently return empty on 404 (residential units return 404 from this endpoint)
    retry: false,
  });

  const showEmployeePicker = enterpriseEmployees.length > 0;

  // Clear employee selection when unit changes
  useEffect(() => {
    setForm((prev) => ({ ...prev, hostEmployeeUserId: '' }));
  }, [form.unitId]);

  // ── Sync pendingWalkIn to sessionStorage ──
  // Persists approval state across remounts so visitor details are not lost
  // if the guard navigates to the Gate Queue and back during an approval wait.
  useEffect(() => {
    writePendingWalkIn(pendingWalkIn);
    if (pendingWalkIn?.id && !activeRequestId) {
      setActiveRequestId(pendingWalkIn.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingWalkIn]);

  // ── Walk-in submission
  const submitMutation = useMutation({
    mutationFn: createWalkIn,
    onSuccess: (data: WalkInRequest) => {
      setActiveRequestId(data.id);
      const unit = units.find((u) => u.id === form.unitId);
      setPendingWalkIn({
        id: data.id,
        visitorName: data.visitorName,
        visitorPhone: form.visitorPhone,
        unitBeingVisited: unit?.unitReference ?? '',
        guardName: '',
        status: data.status,
        createdAt: new Date().toISOString(),
        expiresAt: data.expiresAt,
      });
    },
  });

  // Poll for approval status
  const { walkIn: polledStatus } = useWalkInStatus(activeRequestId);

  // Merge stored display info with latest polled status
  const walkIn: WalkInRequest | null = pendingWalkIn
    ? { ...pendingWalkIn, status: polledStatus?.status ?? pendingWalkIn.status }
    : null;

  // ── Validation ──
  function validate(): FormErrors {
    const errs: FormErrors = {};
    if (!form.visitorName.trim()) {
      errs.visitorName = t('common.required');
    }
    if (!form.visitorPhone.trim() || !validatePhone(form.visitorPhone)) {
      errs.visitorPhone = t('guard.walkIn.invalidPhone');
    }
    if (!form.unitId) {
      errs.unitId = t('guard.walkIn.unitRequired');
    }
    return errs;
  }

  function handleSubmit() {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const idFields = form.idType
      ? {
          idType: form.idType as 'national_id' | 'passport' | 'military_id',
          idNumber: form.idNumber,
        }
      : {};
    const body: CreateWalkInBody = {
      visitorName: form.visitorName.trim(),
      visitorPhone: normalizeKePhoneToE164(form.visitorPhone) ?? form.visitorPhone.trim(),
      unitId: form.unitId,
      ...idFields,
      ...(form.hostEmployeeUserId ? { hostEmployeeUserId: form.hostEmployeeUserId } : {}),
      ...(form.vehiclePlate.trim() ? { vehiclePlate: form.vehiclePlate.trim() } : {}),
    };
    submitMutation.mutate(body);
  }

  function handleReset() {
    setForm(INITIAL_FORM);
    setErrors({});
    setUnitSearch('');
    setUnitOpen(false);
    setActiveRequestId(null);
    setPendingWalkIn(null);
    writePendingWalkIn(null);
    submitMutation.reset();
  }

  function handleRetry() {
    if (pendingWalkIn) {
      setActiveRequestId(null);
      setPendingWalkIn(null);
      submitMutation.reset();
      handleSubmit();
    }
  }

  function updateField(field: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  // ── Offline state ──
  if (!isOnline) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl text-[var(--text-primary)]">{t('guard.walkIn.title')}</h1>
        <div className="rounded-[var(--radius-lg)] border border-[var(--status-amber)] bg-[var(--status-amber-tint)] p-6 text-center">
          <p className="text-sm text-[var(--status-amber-text)]">
            {t('guard.walkIn.offlineMessage')}
          </p>
        </div>
      </div>
    );
  }

  // ── Approval status view ──
  if (walkIn) {
    return (
      <div className="mx-auto max-w-md space-y-4">
        <h1 className="text-xl text-[var(--text-primary)]">{t('guard.walkIn.title')}</h1>
        <WalkInApprovalStatus
          walkIn={walkIn}
          onAllowEntry={handleReset}
          onSendAway={handleReset}
          onRetry={handleRetry}
          isConfirming={false}
        />
      </div>
    );
  }

  // ── Registration form ──
  return (
    <div className="mx-auto max-w-lg space-y-6">
      <div className="flex items-center gap-3">
        <UserPlus className="size-6 text-[var(--text-accent)]" />
        <h1 className="text-xl text-[var(--text-primary)]">{t('guard.walkIn.title')}</h1>
      </div>

      <div className="space-y-4 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--bg-surface)] p-6">
        {/* Visitor Name */}
        <div className="space-y-2">
          <Label htmlFor="visitor-name">{t('guard.walkIn.visitorName')}</Label>
          <Input
            id="visitor-name"
            value={form.visitorName}
            onChange={(e) => updateField('visitorName', e.target.value)}
            placeholder={t('guard.walkIn.visitorNamePlaceholder')}
            aria-describedby={errors.visitorName ? 'visitor-name-error' : undefined}
            aria-invalid={!!errors.visitorName}
          />
          {errors.visitorName && (
            <p id="visitor-name-error" className="text-xs text-[var(--text-error)]" role="alert">
              {errors.visitorName}
            </p>
          )}
        </div>

        {/* Phone Number */}
        <div className="space-y-2">
          <Label htmlFor="visitor-phone">{t('guard.walkIn.visitorPhone')}</Label>
          <Input
            id="visitor-phone"
            type="tel"
            value={form.visitorPhone}
            onChange={(e) => updateField('visitorPhone', e.target.value)}
            placeholder="+254712345678"
            aria-describedby={errors.visitorPhone ? 'visitor-phone-error' : undefined}
            aria-invalid={!!errors.visitorPhone}
          />
          {errors.visitorPhone && (
            <p id="visitor-phone-error" className="text-xs text-[var(--text-error)]" role="alert">
              {errors.visitorPhone}
            </p>
          )}
        </div>

        {/* ID Type & Number */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="id-type">
              {t('guard.walkIn.idType')}{' '}
              <span className="text-[var(--text-tertiary)]">({t('common.optional')})</span>
            </Label>
            <Select value={form.idType} onValueChange={(v) => updateField('idType', v)}>
              <SelectTrigger id="id-type" className="min-h-[44px]">
                <SelectValue placeholder={t('guard.walkIn.idTypePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {ID_TYPES.map((idType) => (
                  <SelectItem key={idType.value} value={idType.value}>
                    {t(idType.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="id-number">
              {t('guard.walkIn.idNumber')}{' '}
              <span className="text-[var(--text-tertiary)]">({t('common.optional')})</span>
            </Label>
            <Input
              id="id-number"
              value={form.idNumber}
              onChange={(e) => updateField('idNumber', e.target.value)}
              placeholder={t('guard.walkIn.idNumberPlaceholder')}
              disabled={!form.idType}
            />
          </div>
        </div>

        {/* Vehicle Plate (optional) */}
        <div className="space-y-2">
          <Label htmlFor="vehicle-plate">
            {t('guard.walkIn.vehiclePlate')}{' '}
            <span className="text-[var(--text-tertiary)]">({t('common.optional')})</span>
          </Label>
          <Input
            id="vehicle-plate"
            value={form.vehiclePlate}
            onChange={(e) => updateField('vehiclePlate', e.target.value.toUpperCase())}
            placeholder={t('guard.walkIn.vehiclePlatePlaceholder')}
          />
        </div>

        {/* Unit Selector — single-tenant buildings show the company name as
            static text instead of a one-option Select. */}
        {isSingleTenantBuilding ? (
          <div className="space-y-2">
            <Label>{t('guard.walkIn.unit')}</Label>
            <div
              className="flex min-h-[44px] items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--bg-muted)] px-3 py-2"
              data-testid="walkin-sole-unit"
            >
              <Building2 className="size-4 text-[var(--text-tertiary)]" />
              <span className="text-sm text-[var(--text-primary)]">{buildingCompanyName}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-2" ref={unitRef}>
            <Label htmlFor="unit-search">{t('guard.walkIn.unit')}</Label>
            <div className="relative">
              <Input
                id="unit-search"
                value={unitSearch}
                onChange={(e) => handleUnitSearchChange(e.target.value)}
                onFocus={() => setUnitOpen(true)}
                placeholder={t('guard.walkIn.unitPlaceholder')}
                autoComplete="off"
                role="combobox"
                aria-expanded={unitOpen && filteredUnits.length > 0}
                aria-haspopup="listbox"
                aria-controls="unit-listbox"
                aria-describedby={errors.unitId ? 'unit-error' : undefined}
                aria-invalid={!!errors.unitId}
                className="min-h-[44px]"
              />
              {unitOpen && filteredUnits.length > 0 && (
                <div
                  id="unit-listbox"
                  role="listbox"
                  aria-label={t('guard.walkIn.unit')}
                  className="absolute z-10 mt-1 w-full overflow-hidden rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-md"
                >
                  {filteredUnits.map((unit) => (
                    <button
                      key={unit.id}
                      type="button"
                      role="option"
                      aria-selected={form.unitId === unit.id}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors',
                        'hover:bg-[var(--bg-muted)]',
                        form.unitId === unit.id && 'bg-[var(--bg-accent-tint)]',
                      )}
                      onMouseDown={(e) => {
                        e.preventDefault(); // keep input focused until selection
                        handleUnitSelect(unit.id, unit.unitReference);
                      }}
                    >
                      <Building2 className="size-3.5 shrink-0 text-[var(--text-tertiary)]" />
                      <span
                        className={cn(
                          'flex-1 text-[var(--text-primary)]',
                          form.unitId === unit.id && 'font-medium text-[var(--text-accent)]',
                        )}
                      >
                        {unit.unitReference}
                      </span>
                      {unit.zoneName && (
                        <span className="text-xs text-[var(--text-tertiary)]">{unit.zoneName}</span>
                      )}
                      {form.unitId === unit.id && (
                        <Check className="size-3.5 shrink-0 text-[var(--text-accent)]" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {errors.unitId && (
              <p id="unit-error" className="text-xs text-[var(--text-error)]" role="alert">
                {errors.unitId}
              </p>
            )}
          </div>
        )}

        {/* Enterprise employee picker — only shown for enterprise units with active employees.
            Optional: if omitted the walk-in routes to the enterprise tenant admin. */}
        {showEmployeePicker && (
          <div className="space-y-2">
            <Label htmlFor="host-employee">
              {t('guard.walkIn.hostEmployee')}{' '}
              <span className="text-[var(--text-tertiary)]">({t('common.optional')})</span>
            </Label>
            <Select
              value={form.hostEmployeeUserId}
              onValueChange={(v) => updateField('hostEmployeeUserId', v === '__none__' ? '' : v)}
            >
              <SelectTrigger id="host-employee" className="min-h-[44px]">
                <SelectValue placeholder={t('guard.walkIn.hostEmployeePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">{t('guard.walkIn.hostEmployeeAdmin')}</SelectItem>
                {enterpriseEmployees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.fullName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[var(--text-tertiary)]">
              {t('guard.walkIn.hostEmployeeHint')}
            </p>
          </div>
        )}

        {/* API error */}
        {submitMutation.error && (
          <p className="text-sm text-[var(--text-error)]" role="alert">
            {(submitMutation.error as { message?: string })?.message ?? t('common.error')}
          </p>
        )}

        {/* Submit */}
        <Button
          className="min-h-[44px] w-full"
          onClick={handleSubmit}
          disabled={submitMutation.isPending}
        >
          {submitMutation.isPending && <RefreshCw className="mr-2 size-4 animate-spin" />}
          {t('guard.walkIn.submitButton')}
        </Button>
      </div>
    </div>
  );
}
